import React, { ChangeEvent, FormEvent, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { zenworksServiceApi } from '../../service/zenworksService.api';
import { globalCards } from '../../utils/global.card';
import getTimeStamp from '../../utils/getTimeStamp';
import { useDispatch } from 'react-redux';
import { ADD_EVENT } from '../../redux/reducers/eventReducer';
import { generateRandomId } from '../../utils/global.utils';
import { debounce } from '../../utils/debounce';
import {  stateOptions,  stateFormOptions,} from '../../utils/constentData/stateFiling';

interface DynamicFormProps {
  // handleFormSubmit: (response: any, formFields?: any) => void;
}

type ServiceType = {
  service: string;
  formCount: number;
  foreignMailCount?: number;
  stateFormName?: string;
  stateName?: string;
};

const INITIAL_SERVICE_STATE: ServiceType = {
  formCount: 100,
  service: '1099',
  foreignMailCount: 0,
  stateFormName: '',
  stateName: '',
};

const serviceOptions = [
  {
    name: 'IRS eFile 1099, 1098, 1095, W-2, W-2C, W-2G',
    value: '1099',
  },
  {
    name: 'IRS eFile 5498, 3921, 3922, 1097-BTC, 592-B',
    value: '5498'
  },
  {
    name: 'IRS eFile 940, 941, 941SP, 941-X',
    value: '94'
  },
  {
    name: 'IRS eFile 944, 944-X, 945, 943, 943-X',
    value: '94'
  },
  { name: 'State Filing', value: 'State' },
  {
    name: 'IRS eFile 1042-S,1042',
    value: '1042',
  },
  { name: 'Real Time TIN Match', value: 'Real Time TIN Match' }, 
  { name: 'Mailing', value: 'Mailing' },
  { name: 'W8/W9 Requests', value: 'W8/W9' },
  { name: "8809, 8027, 8955", value: "8809"}
];

// const foreignMailCountServices = ['1099', '1042', 'Mailing', 'W2VI/W2GU'];

const isStateFilingReady = ({ service, stateName, stateFormName }: ServiceType) =>
  service !== 'State' || (!!stateName && !!stateFormName);

const PricingCalculator: React.FC<DynamicFormProps> = () => {
  const connectionStatus = useSelector(
    (state: RootState) => state.socketConnection.status,
  );

  const [formFields, setFormFields] = useState<ServiceType[]>([
    INITIAL_SERVICE_STATE,
  ]);
  const [totalAmt, setTotalAmt] = useState('2.33');
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const fetchPricing = useCallback(async (updatedFormFields: ServiceType[]) => {
    const formCount = Number(updatedFormFields[0].formCount);
    
    // Don't make API call for zero or negative values
    if (formCount <= 0) {
      setIsLoading(false);
      setTotalAmt('0.00');
      return;
    }
    
    setIsLoading(true);
    if (formCount > 1000) {
      setIsLoading(false);
      setTotalAmt('0.00');
      return;
    }
    if (!isStateFilingReady(updatedFormFields[0])) {
      setIsLoading(false);
      return;
    }
    const res =
      await zenworksServiceApi.getPricingCalculator(updatedFormFields);
    const computedPrice = ((res && res.content && res.content.result) ||
      0) as number;
    // @ts-expect-error - will fix it later
    let pricePerForm = parseFloat(computedPrice / formCount) as number;
    pricePerForm = isNaN(pricePerForm) ? 0 : pricePerForm;
    setTotalAmt(pricePerForm.toFixed(2));
    setIsLoading(false);
  }, []);

  const fetchPricingDebounce = useMemo(
    () => debounce(fetchPricing, 900),
    [fetchPricing],
  );

  const handleChangeField = async (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number,
  ) => {
    e.preventDefault();
    const { name, value } = e.target;
    
    // For number inputs, ensure non-negative values
    let processedValue: string | number = value;
    if (name === 'formCount') {
      const numValue = Number(value);
      // Prevent negative numbers - set to 0 if negative
      processedValue = numValue < 0 ? 0 : numValue;
    }
    
    const updatedFormFields = formFields.map((field, idx) =>
      index === idx
        ? {
            ...field,
            foreignMailCount:
              e.target.name !== 'service' ? field.foreignMailCount : 0,
            stateFormName: name !== 'service' ? field.stateFormName : '',
            stateName: name !== 'service' ? field.stateName : '',
            [name]: processedValue,
          }
        : field,
    );
    setFormFields(updatedFormFields);
    
    // Only call API if formCount is greater than 0
    if (name === 'formCount') {
      const formCount = Number(processedValue);
      if (formCount > 0) {
        fetchPricingDebounce(updatedFormFields);
      } else {
        setTotalAmt('0.00');
      }
    } else {
      // For service changes, check if formCount > 0 before calling API
      const formCount = Number(updatedFormFields[0].formCount);
      if (formCount > 0 && isStateFilingReady(updatedFormFields[0])) {
        fetchPricingDebounce(updatedFormFields);
      }
    }
  };

  // const handleChangeFieldDebounce = debounce(handleChangeField, 300)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const res = await zenworksServiceApi.getPricingCalculator(formFields);
    setTotalAmt(res && res.content && res.content.result);
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const activeElement = document.activeElement as HTMLElement;

    if (
      activeElement.nodeName === 'INPUT' &&
      activeElement.getAttribute('type') === 'number'
    ) {
      e.preventDefault();
    }
  };

  const handleContactSales = () => {
    const contactSalesCard = globalCards['contactSale'];
    const timeStamp = getTimeStamp();
    const divId = generateRandomId();
    dispatch({
      type: ADD_EVENT,
      payload: {
        ...contactSalesCard,
        id: divId,
        divId,
        user: 'actionCard',
        time: timeStamp,
        senderType: 'chatBot',
      },
    });
  };

  return (
    <div className="m-3 relative">
      {isLoading && (
        <div className="w-[100%] flex justify-center items-center">
          <button
            type="button"
            className="flex justify-center items-center"
            disabled
          >
            {/* <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24" fill="red">
      </svg> */}
            <svg
              className="animate-spin h-10 w-10 mr-3 flex justify-center items-center"
              version="1.1"
              id="loader-1"
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              width="40px"
              height="40px"
              viewBox="0 0 50 50"
            >
              <path
                fill="#000"
                d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z"
              ></path>
            </svg>
          </button>
        </div>
      )}
      <div className="mb-4 mx-3 ">
        Step into simplified pricing! Estimate through our pricing calculator
      </div>
      <div className="flex flex-wrap">
        <form onSubmit={handleSubmit}>
          {formFields.map((field, index) => (
            <div
              key={`services-${index}`}
              className="flex flex-wrap gap-3 gap-y-6 py-2 relative"
            >
              <div className="w-[100%]">
                <label htmlFor={`service-${index}`}>Service</label>
                <select
                  className="block appearance-none w-full bg-white border-2 border-gray-300 hover:border-secondary px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline"
                  name="service"
                  id={`service-${index}`}
                  value={field.service}
                  onChange={(e) => handleChangeField(e, index)}
                  disabled={connectionStatus !== 'Connected'}
                >
                  <option value="">Select Form</option>
                  {serviceOptions.map(({ name, value }: any, index: any) => (
                    <option key={`service-options-${index}`} value={value}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              {field.service === 'State' &&
                [
                  {
                    name: 'stateFormName',
                    label: 'Form Name',
                    options: stateFormOptions,
                  },
                  { name: 'stateName', label: 'State', options: stateOptions },
                ].map(({ name, label, options }) => (
                  <div key={name} className="w-[100%]">
                    <label htmlFor={`${name}-${index}`}>{label}</label>
                    <select
                      className="block appearance-none w-full bg-white border-2 border-gray-300 hover:border-secondary px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline"
                      name={name}
                      id={`${name}-${index}`}
                      value={field[name as 'stateName' | 'stateFormName']}
                      onChange={(e) => handleChangeField(e, index)}
                      disabled={connectionStatus !== 'Connected'}
                    >
                      <option value="">Select {label}</option>
                      {options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              <div className="w-[48%]" onWheel={handleWheel}>
                <label htmlFor={`formCount-${index}`}>Form Count</label>
                <input
                  type="number"
                  name="formCount"
                  id={`formCount-${index}`}
                  value={field.formCount}
                  min="0"
                  onChange={(e) => handleChangeField(e, index)}
                  onKeyDown={(e) => {
                    // Check if the pressed key is Enter and prevent the default behavior
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                    if (
                      (e.key === '-' || e.key === '+') &&
                      e.target instanceof HTMLInputElement
                    ) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Form Count"
                  className="w-full focus:outline-none focus:placeholder-gray-400 text-gray-600 placeholder-gray-600 pl-3 bg-gray-100 border-2 border-gray-200 focus:border-primary rounded-lg py-2"
                  disabled={connectionStatus !== 'Connected'}
                />
              </div>
              <div
                className="w-[48%] flex justify-center items-center"
              >
                {formFields[0].formCount > 1000 ? (
                  <button
                    onClick={handleContactSales}
                    // disabled={connectionStatus !== 'Connected'}
                    className="bg-white w-full text-blue-500 font-semibold py-2 px-4 border border-primary rounded"
                  >
                    Contact Sales for Discounted Pricing
                  </button>
                ) : (
                  <h1>
                    <strong className="text-3xl border-black border-b-4">
                      ${totalAmt}
                    </strong>
                    each
                  </h1>
                )}
              </div>
            </div>
          ))}
        </form>
      </div>
    </div>
  );
};

export default PricingCalculator;
